import { boundMethod } from 'autobind-decorator';
import { authManager } from 'lib-shared-auth';
import { config } from 'lib-shared-backend-config';
import { Sentry } from 'lib-shared-sentry';
import { ampli } from 'lib-shared-tracking';
import noop from 'lodash/noop';
import { v4 as uuidv4 } from 'uuid';

import { debugLog } from 'utils/debugLog';

import {
  InboundWebsocketEvent,
  WebsocketMessageType,
} from '../types/inboundWebsocketEvent';
import {
  getWorkerConnection,
  resetWorkerConnection,
} from '../worker/getWorkerConnection';
import {
  GetDataFulfilledMessage,
  PortId,
  WorkerMessage,
  WorkerRequestMessage,
} from '../worker/types';

import { WEBSOCKET_MIN_RECOVERY_INTERVAL_MS } from './constants';
import { SocketInterface } from './types';
import { WebSocketWatchdog } from './webSocketWatchdog';

type onPrimaryConnectionChange = (isActive: boolean) => void;

const REQUEST_TIMEOUT_MS = 10_000;
const HEARTBEAT_STALE_THRESHOLD_MS = 15_000;
const VISIBILITY_RECONNECT_MIN_INTERVAL_MS = 5_000;

export class SharedWorkerImplementation implements SocketInterface {
  private _worker = getWorkerConnection();
  private _isConnected = false;
  private _onWebSocketClosed = noop;
  private _portId: PortId | null = null;
  private _previousPortId: PortId | null = null;
  private _onPrimaryConnectionChange: onPrimaryConnectionChange = noop;

  private _onMessageHandler:
    | ((message: WorkerMessage | InboundWebsocketEvent) => void)
    | null = null;
  private _onErrorHandler: ((error: Event) => void) | null = null;

  private _started = false;
  private _lastHeartbeatAt = Date.now();
  private _visibilityReconnectInFlight = false;
  private _lastVisibilityReconnectAt = 0;
  private _recoveryInFlight = false;
  private _lastRecoveryAt = 0;

  private _watchdog = new WebSocketWatchdog({
    probe: async () => {
      const staleMs = Date.now() - this._lastHeartbeatAt;
      if (staleMs > HEARTBEAT_STALE_THRESHOLD_MS) {
        throw new Error(`Worker heartbeat stale (${staleMs}ms)`);
      }
    },
    onDead: () => this.recoverWorkerConnection(),
  });

  get isConnected(): boolean {
    return this._isConnected;
  }

  get portId(): PortId | null {
    return this._portId;
  }

  private postMessage(message: WorkerMessage) {
    this._worker.port.postMessage(message);
  }

  async start(): Promise<void> {
    const authData = await authManager.getAuthData();

    if (authData?.access_token) {
      this.postMessage({
        type: 'worker:connect-to-websocket',
        payload: {
          appToken: authData?.access_token,
          websocketUrl: config.url.webSocket,
        },
      });
    }

    if (!this._started) {
      this._started = true;
      window.addEventListener('beforeunload', this.onBeforeUnloadHandler);
      window.addEventListener('focus', this.onWindowFocusHandler);
      document.addEventListener(
        'visibilitychange',
        this.onVisibilityChangeHandler,
      );
    }

    this._watchdog.start();
  }

  close(): void {
    this.postMessage({
      type: 'worker:close-websocket',
    });

    this.teardownTabListeners();
  }

  disconnectPort(): void {
    if (this._portId) {
      this.postMessage({
        type: 'worker:disconnect-port',
        payload: { id: this._portId },
      });
    }

    this._portId = null;
    this._isConnected = false;
    this.teardownTabListeners();
    // Shared socket may stay open for other tabs; drive Redux teardown locally.
    this._onWebSocketClosed();
  }

  private teardownTabListeners(): void {
    this._watchdog.stop();
    this.onError(noop);
    window.removeEventListener('focus', this.onWindowFocusHandler);
    window.removeEventListener('beforeunload', this.onBeforeUnloadHandler);
    document.removeEventListener(
      'visibilitychange',
      this.onVisibilityChangeHandler,
    );
    this._started = false;
  }

  @boundMethod
  private onBeforeUnloadHandler() {
    if (this._portId) {
      this.postMessage({
        type: 'worker:disconnect-port',
        payload: { id: this._portId },
      });
    }

    this.sendAndWaitForResponse('worker:get-number-of-connections').then(
      (response) => {
        if (!response) return;
        const { payload } = response as {
          payload: { numberOfConnections: number };
        };

        if (ampli.isLoaded) {
          ampli.sharedWorkerTabOrWindowClosed({
            numberOfInstances: payload.numberOfConnections,
          });
        }
      },
    );
  }

  @boundMethod
  private async onVisibilityChangeHandler() {
    if (document.visibilityState !== 'visible') return;
    if (this._recoveryInFlight) return;

    const staleMs = Date.now() - this._lastHeartbeatAt;
    if (staleMs > HEARTBEAT_STALE_THRESHOLD_MS) {
      this.recoverWorkerConnection();
      return;
    }

    // Avoid reconnect churn on normal tab switching when the worker is healthy.
    if (this._isConnected) return;

    const now = Date.now();
    if (
      this._visibilityReconnectInFlight ||
      now - this._lastVisibilityReconnectAt <
        VISIBILITY_RECONNECT_MIN_INTERVAL_MS
    ) {
      return;
    }

    this._visibilityReconnectInFlight = true;
    this._lastVisibilityReconnectAt = now;
    try {
      const authData = await authManager.getAuthData();
      if (authData?.access_token) {
        this.postMessage({
          type: 'worker:connect-to-websocket',
          payload: {
            appToken: authData.access_token,
            websocketUrl: config.url.webSocket,
          },
        });
      }
    } finally {
      this._visibilityReconnectInFlight = false;
    }
  }

  @boundMethod
  private onWindowFocusHandler() {
    this.postMessage({
      type: 'worker:port-activated',
      payload: {
        id: this._portId as string,
      },
    });
  }

  // -- Recovery --

  private async recoverWorkerConnection(): Promise<void> {
    if (this._recoveryInFlight) return;

    const now = Date.now();
    if (now - this._lastRecoveryAt < WEBSOCKET_MIN_RECOVERY_INTERVAL_MS) return;

    this._recoveryInFlight = true;
    this._lastRecoveryAt = now;
    this._isConnected = false;

    try {
      // Detach the old port first so the worker stops relaying events to it.
      if (this._portId) {
        this.postMessage({
          type: 'worker:disconnect-port',
          payload: { id: this._portId },
        });
        this._portId = null;
      }

      resetWorkerConnection();
      this._worker = getWorkerConnection();

      this.rebindWorkerHandlers();

      const authData = await authManager.getAuthData();
      if (authData?.access_token) {
        this.postMessage({
          type: 'worker:connect-to-websocket',
          payload: {
            appToken: authData.access_token,
            websocketUrl: config.url.webSocket,
          },
        });
      }

      this._lastHeartbeatAt = Date.now();
      this._watchdog.start();
    } catch (e) {
      debugLog('SHARED-WORKER', 'Recovery failed', {
        error: e instanceof Error ? e.message : String(e),
      });
      Sentry.addBreadcrumb({
        category: 'sharedWorker',
        message: `Recovery failed: ${e}`,
        level: 'warning',
      });
    } finally {
      this._recoveryInFlight = false;
    }
  }

  private rebindWorkerHandlers(): void {
    if (this._onMessageHandler) {
      this.onMessage(this._onMessageHandler);
    }
    if (this._onErrorHandler) {
      this.onError(this._onErrorHandler);
    }
  }

  // -- Request/response helpers --

  private waitForMessageAndResolve(
    resolve: (message: unknown) => void,
    predicate: (message: unknown) => boolean,
    options?: { timeoutMs?: number },
  ) {
    const port = this._worker.port;
    let settled = false;

    const listener = (event: MessageEvent) => {
      try {
        const message: InboundWebsocketEvent | WorkerMessage =
          typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        if (predicate(message)) {
          settled = true;
          port.removeEventListener('message', listener);
          resolve(message as GetDataFulfilledMessage);
        }
      } catch (e) {
        debugLog('SHARED-WORKER', 'Error parsing worker message', {
          rawData: event.data,
        });
        Sentry.addBreadcrumb({
          category: 'sharedWorker',
          message: 'Error parsing worker message',
          level: 'warning',
          data: { event },
        });
      }
    };

    port.addEventListener('message', listener);

    if (options?.timeoutMs) {
      setTimeout(() => {
        if (!settled) {
          settled = true;
          port.removeEventListener('message', listener);
          resolve(undefined);
        }
      }, options.timeoutMs);
    }
  }

  waitForMessage(type: WebsocketMessageType): Promise<InboundWebsocketEvent> {
    return new Promise((resolve) => {
      this.waitForMessageAndResolve(
        resolve as (message: unknown) => void,
        (message) =>
          typeof message === 'object' &&
          message !== null &&
          'type' in message &&
          (message as { type: string }).type === type,
      );
    });
  }

  send(message: WorkerMessage): void {
    if (this._isConnected) {
      this.postMessage(message);
    } else {
      debugLog('SHARED-WORKER', 'WS not ready');
      Sentry.addBreadcrumb({
        category: 'sharedWorker',
        message: 'WS: ws is not ready',
        level: 'warning',
      });
    }
  }

  sendAndWaitForResponse(type: WorkerRequestMessage['type']): Promise<unknown> {
    return new Promise((resolve) => {
      const id = uuidv4();

      this.waitForMessageAndResolve(
        (message) => {
          if (message === undefined) {
            debugLog('SHARED-WORKER', 'sendAndWaitForResponse timed out', {
              type,
            });
          }
          resolve(message);
        },
        (message: unknown) =>
          typeof message === 'object' &&
          message !== null &&
          'payload' in message &&
          (message as WorkerRequestMessage)?.payload?.requestId === id,
        { timeoutMs: REQUEST_TIMEOUT_MS },
      );

      this.postMessage({
        type,
        payload: { requestId: id },
      } as WorkerRequestMessage);
    });
  }

  onClose(handler: () => void): void {
    this._onWebSocketClosed = handler;
  }

  onMessage(
    onMessageHandler: (message: WorkerMessage | InboundWebsocketEvent) => void,
  ): void {
    this._onMessageHandler = onMessageHandler;

    this._worker.port.onmessage = (event) => {
      const data: WorkerMessage | InboundWebsocketEvent =
        typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

      if ('type' in data) {
        if (data.type === 'worker:heartbeat') {
          this._lastHeartbeatAt = Date.now();
          return;
        }

        switch (data.type) {
          case 'worker:port-connected':
            this._portId = data.payload.id;

            this.sendAndWaitForResponse(
              'worker:get-number-of-connections',
            ).then((response) => {
              if (!response) return;
              const { payload } = response as {
                payload: { numberOfConnections: number };
              };

              if (ampli.isLoaded) {
                ampli.sharedWorkerTabOrWindowOpened({
                  numberOfInstances: payload.numberOfConnections,
                });
              }
            });

            break;
          case 'worker:connected-to-websocket':
            this._isConnected = true;
            break;
          case 'worker:websocket-closed':
            Sentry.captureMessage('WebSocket: Close event received', {
              level: 'log',
              extra: data.payload,
            });
            ampli.websocketCloseEventReceived(data.payload);

            this._isConnected = false;
            this._onWebSocketClosed();
            break;
          case 'worker:primary-port-changed':
            if (
              data.payload.id === this._portId &&
              this._previousPortId !== this.portId &&
              ampli.isLoaded
            ) {
              ampli.sharedWorkerTabOrWindowChanged();
            }

            this._onPrimaryConnectionChange(data.payload.id === this._portId);
            this._previousPortId = data.payload.id;
            break;
        }
      }

      onMessageHandler(data);
    };
  }

  onError(onErrorHandler: (error: Event) => void): void {
    this._onErrorHandler = onErrorHandler;

    this._worker.onerror = (error) => {
      debugLog('SHARED-WORKER', 'WS error event received', {
        errorType: error.type,
      });
      Sentry.addBreadcrumb({
        category: 'sharedWorker',
        message: 'WS: Error event received',
        level: 'warning',
        data: { error },
      });
      onErrorHandler(error);
    };
  }

  onPrimaryConnectionChange(handler: onPrimaryConnectionChange): void {
    this._onPrimaryConnectionChange = handler;
  }
}
